import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { DataManagerComponent } from '@organization-management/data-manager/data-manager.component';
import { OrganizationDeveloperFurtherInformationComponent } from '@organization-management/organization/developer-information/further-information/organization-developer-further-information.component';
import { OrganizationDeveloperPublicProfileComponent } from '@organization-management/organization/developer-information/public-profile/organization-developer-public-profile.component';
import { OrganizationInvestorFurtherInformationComponent } from '@organization-management/organization/investor-information/further-information/organization-investor-further-information.component';
import { OrganizationInvestorPublicProfileComponent } from '@organization-management/organization/investor-information/public-profile/organization-investor-public-profile.component';
import { OrganizationNavigationComponent } from '@organization-management/organization/organization-navigation/organization-navigation.component';
import { OrganizationProfileComponent } from '@organization-management/organization/organization-profile/organization-profile.component';
import { OrganizationUnsavedGuard } from '@organization-management/organization/shared/guard/organization-unsaved.guard';
import { OrganizationVendorPublicProfileComponent } from '@organization-management/organization/vendor-information/public-profile/organization-vendor-public-profile.component';
import { FormioFileUploaderComponent } from '@program/shared/formio-custom-components/formio-file-uploader/formio-file-uploader.component';
import { ExportDownloadComponent } from '@shared/components/files/export-download/export-download.component';
import { GlobalFilesComponent } from '@shared/components/files/global-files/global-files.component';
import { UserAdminComponent } from '@shared/components/manage-users/user-admin.component';
import { PageNotFoundComponent } from '@shared/components/page-not-found/page-not-found.component';
import { UserProfileComponent } from '@user/profile/user-profile.component';

import { HomeComponent } from './home/home.component';
import { MainNavigationComponent } from './main-navigation/main-navigation.component';
import { TaskRouteGuard } from './task/task-route.guard';
import { TaskComponent } from './task/task.component';
import { CompletedTaskComponent } from './task/completed-task/completed-task.component';
import { SccRedirectGuard } from '@global/guards/scc-redirect.guard';
import { TokenCheckGuard } from '@global/guards/token-check.guard';
import { SccGuard } from '@global/guards/scc.guard';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'oes',
    pathMatch: 'full',
  },
  {
    path: 'task/:id',
    component: TaskComponent,
    canActivate: [TaskRouteGuard]
  },
  {
    path: 'task/:id/completed',
    component: CompletedTaskComponent,
    canActivate: [TaskRouteGuard],
  },
  {
    path: 'standard-api',
    loadChildren: () => import('./public/standard-api/standard-api.router.module').then(m => m.StandardApiRouterModule),
  },
  {
    path: 'oes',
    component: MainNavigationComponent,
    canActivateChild: [TokenCheckGuard],
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'home',
        component: HomeComponent,
        canActivate: [SccRedirectGuard],
        data: {
          breadcrumb: 'main-navigation.home',
        },
      },
      {
        // User profile is accessible by any type of user role.
        path: 'user-profile',
        component: UserProfileComponent,
        data: {
          breadcrumb: 'user-profile.account-settings-header',
        },
      },
      {
        path: 'admin',
        canActivate: [SccRedirectGuard],
        loadChildren: () =>
          import('./admin/admin.module').then((m) => m.AdminModule),
      },
      {
        path: 'credit',
        canActivate: [SccGuard],
        loadChildren: () => import('./credit/credit.module').then(m => m.CreditModule),
      },
      {
        path: 'organization-management',
        canActivate: [SccRedirectGuard],
        children: [
          {
            path: 'users',
            component: UserAdminComponent,
            data: {
              breadcrumb: 'main-navigation.organization-users',
            },
          },
          {
            path: 'data-manager',
            component: DataManagerComponent,
            data: {
              breadcrumb: 'main-navigation.data-manager',
            },
          },
          {
            path: 'profile',
            component: OrganizationNavigationComponent,
            data: {
              breadcrumb: '',
            },
            children: [
              {
                path: '',
                redirectTo: 'organization-profile',
                pathMatch: 'full',
              },
              {
                path: 'organization-profile',
                component: OrganizationProfileComponent,
                data: {
                  breadcrumb: 'profile-nav.org-profile',
                },
                canDeactivate: [OrganizationUnsavedGuard],
              },
              {
                path: 'developer',
                children: [
                  {
                    path: '',
                    redirectTo: 'public-profile',
                    pathMatch: 'full',
                  },
                  {
                    path: 'public-profile',
                    component: OrganizationDeveloperPublicProfileComponent,
                    data: {
                      breadcrumb: 'profile-nav.public-profile',
                    },
                    canDeactivate: [OrganizationUnsavedGuard],
                  },
                  {
                    path: 'further-information',
                    component: OrganizationDeveloperFurtherInformationComponent,
                    data: {
                      breadcrumb: 'profile-nav.further-info',
                    },
                    canDeactivate: [OrganizationUnsavedGuard],
                  },
                ],
              },
              {
                path: 'investor',
                children: [
                  {
                    path: '',
                    redirectTo: 'public-profile',
                    pathMatch: 'full',
                  },
                  {
                    path: 'public-profile',
                    component: OrganizationInvestorPublicProfileComponent,
                    data: {
                      breadcrumb: 'profile-nav.public-profile',
                    },
                    canDeactivate: [OrganizationUnsavedGuard],
                  },
                  {
                    path: 'further-information',
                    component: OrganizationInvestorFurtherInformationComponent,
                    data: {
                      breadcrumb: 'profile-nav.further-info',
                    },
                    canDeactivate: [OrganizationUnsavedGuard],
                  },
                ],
              },
              {
                path: 'vendor',
                children: [
                  {
                    path: '',
                    redirectTo: 'public-profile',
                    pathMatch: 'full',
                  },
                  {
                    path: 'public-profile',
                    component: OrganizationVendorPublicProfileComponent,
                    data: {
                      breadcrumb: 'profile-nav.public-profile',
                    },
                    canDeactivate: [OrganizationUnsavedGuard],
                  },
                ],
              },
            ],
          },
        ],
      },
      // Finance
      {
        path: 'finance',
        canActivate: [SccRedirectGuard],
        loadChildren: () =>
          import('./finance/finance.module').then((m) => m.FinanceModule),
        data: {
          breadcrumb: '',
        },
      },
      // Project
      {
        path: 'projects',
        canActivate: [SccRedirectGuard],
        loadChildren: () =>
          import('./project/project.module').then((m) => m.ProjectTabsModule),
        data: {
          breadcrumb: '',
        },
      },
      // RFQ (Quotes)
      {
        path: 'procurement',
        canActivate: [SccRedirectGuard],
        loadChildren: () =>
          import('./procurement/quote/quote.module').then((m) => m.QuoteModule),
        data: {
          breadcrumb: '',
        },
      },
      // Vendor Marketplace
      {
        path: 'vendor-marketplace',
        canActivate: [SccRedirectGuard],
        loadChildren: () =>
          import('./vendor-marketplace/vendor-marketplace.module').then(
            (m) => m.VendorMarketplaceModule
          ),
        data: {
          breadcrumb: '',
        },
      },
      // Files
      {
        path: 'files',
        canActivate: [SccRedirectGuard],
        component: GlobalFilesComponent,
        data: {
          breadcrumb: 'main-navigation.files',
        },
      },
      // Export download landing page (target of email links)
      {
        path: 'exports/:jobId',
        component: ExportDownloadComponent,
        data: {
          breadcrumb: 'files.export-download.breadcrumb',
        },
      },
      // Analytics
      {
        path: 'analytics',
        canActivate: [SccRedirectGuard],
        loadChildren: () =>
          import('./analytics/analytics.module').then((m) => m.AnalyticsModule),
        data: {
          breadcrumb: '',
        },
      },
      {
        path: 'opportunities',
        canActivate: [SccRedirectGuard],
        loadChildren: () =>
          import('./analytics/analytics.module').then((m) => m.AnalyticsModule),
        data: {
          breadcrumb: '',
        },
      },
    ],
  },
  // This is to ensure that this component is added to bundle
  {
    component: FormioFileUploaderComponent,
    path: 'dont-use-this-route-it-wont-work',
  },
  // Use this for 404 NOT FOUND
  {
    path: '**',
    component: PageNotFoundComponent,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // useHash: true,
      initialNavigation: 'disabled',
      urlUpdateStrategy: 'eager',
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
      enableTracing: false,
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
